import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import axios from 'axios';
import ImageViewer from '../../../components/ImageViewer';
import { ROUTES, serviceRequestEdit } from '../../../constants/routes';

function RequestDetails() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [images, setImages] = useState([]);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    fetchRequestDetails();
    fetchProperties();
    fetchUsers();
    fetchImages();
  }, [id]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/requests/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequest(response.data);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProperties(response.data);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`/api/requests/${id}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setImages(response.data);
    } catch (err) {
      console.error('Failed to fetch images:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'Unknown Property';
  };

  const getRequesterName = (requesterId) => {
    const user = users.find(u => u.id === requesterId);
    return user ? user.name : 'Unknown User';
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'badge bg-warning';
      case 'in progress':
        return 'badge bg-info';
      case 'closed':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'badge bg-danger';
      case 'medium':
        return 'badge bg-warning';
      case 'low':
        return 'badge bg-success';
      default:
        return 'badge bg-secondary';
    }
  };

  const openImageViewer = (index) => {
    setSelectedImageIndex(index);
    setImageViewerOpen(true);
  };

  if (loading) {
    return (
      <div className="container mt-4 app-shell-page">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4 app-shell-page">
        <div className="alert alert-danger">
          {error}
        </div>
        <button onClick={() => navigate(ROUTES.SERVICE_REQUESTS)} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="container mt-4 app-shell-page">
        <div className="alert alert-warning">
          Request not found
        </div>
        <button onClick={() => navigate(ROUTES.SERVICE_REQUESTS)} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="container mt-4 app-shell-page">
      <div className="row">
        <div className="col-12">
          <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <h1 className="h3 mb-1">Service Request Details</h1>
              <p className="text-muted small mb-0">Read-only view of this request.</p>
            </div>
            <div className="d-flex flex-wrap gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate(ROUTES.SERVICE_REQUESTS)}
                className="btn btn-outline-secondary btn-sm"
              >
                Back to Dashboard
              </button>
              <button
                type="button"
                onClick={() => navigate(serviceRequestEdit(id))}
                className="btn btn-warning btn-sm"
              >
                Edit Request
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="card app-card mb-4">
            <div className="card-body">
              <h5 className="card-title">Request Details</h5>
              
              <div className="mb-3">
                <label className="form-label fw-bold">Property</label>
                <div className="border rounded p-2 bg-light">
                  {getPropertyName(request.property_id)}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Status</label>
                <div className="border rounded p-2 bg-light">
                  <span className={getStatusBadgeClass(request.status)}>
                    {request.status || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Request Type</label>
                <div className="border rounded p-2 bg-light">
                  {request.request_type || 'N/A'}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Location</label>
                <div className="border rounded p-2 bg-light">
                  {request.location || 'N/A'}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Requester</label>
                <div className="border rounded p-2 bg-light">
                  {getRequesterName(request.requester_id)}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Date</label>
                <div className="border rounded p-2 bg-light">
                  {formatDate(request.date)}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Priority</label>
                <div className="border rounded p-2 bg-light">
                  <span className={getPriorityBadgeClass(request.priority)}>
                    {request.priority || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Tech</label>
                <div className="border rounded p-2 bg-light">
                  {request.tech_name || 'Unassigned'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card app-card mb-4">
            <div className="card-body">
              <h5 className="card-title">Problem Description</h5>
              
              <div className="mb-3">
                <label className="form-label fw-bold">Problem</label>
                <div className="border rounded p-2 bg-light">
                  {request.problem || 'No description provided'}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Notes</label>
                <div className="border rounded p-2 bg-light">
                  {request.notes || 'No notes'}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Follow Up</label>
                <div className="border rounded p-2 bg-light">
                  {request.follow_up || 'No follow up'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="card app-card mb-4">
            <div className="card-body">
              <h5 className="card-title">Additional Information</h5>
              
              <div className="mb-3">
                <label className="form-label fw-bold">Permission Granted</label>
                <div className="border rounded p-2 bg-light">
                  {request.permission ? (
                    <span className="badge bg-success">Yes</span>
                  ) : (
                    <span className="badge bg-secondary">No</span>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Space Occupied</label>
                <div className="border rounded p-2 bg-light">
                  {request.occupied ? (
                    <span className="badge bg-warning">Yes</span>
                  ) : (
                    <span className="badge bg-secondary">No</span>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Time Spent</label>
                <div className="border rounded p-2 bg-light">
                  {request.time_spent || 'N/A'}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Date Finished</label>
                <div className="border rounded p-2 bg-light">
                  {formatDate(request.date_finished)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card app-card mb-4">
            <div className="card-body">
              <h5 className="card-title">Work Order Information</h5>
              
              <div className="mb-3">
                <label className="form-label fw-bold">Work Order Number</label>
                <div className="border rounded p-2 bg-light">
                  <strong>{request.work_order_number}</strong>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Request ID</label>
                <div className="border rounded p-2 bg-light">
                  {request.id}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Created Date</label>
                <div className="border rounded p-2 bg-light">
                  {formatDate(request.date)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Images Section */}
      {images.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Photos</h5>
                <div className="row">
                  {images.map((image, index) => (
                    <div key={image.id} className="col-md-3 mb-3">
                      <div className="border rounded p-2 bg-light">
                        <img 
                          src={image.image_url} 
                          alt={`Service Request Image ${index + 1}`} 
                          className="img-fluid rounded cursor-pointer"
                          style={{ width: '100%', height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => openImageViewer(index)}
                        />
                        <div className="mt-2 text-muted small">
                          Uploaded: {formatDate(image.uploaded_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      <ImageViewer 
        images={images}
        isOpen={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
}

export default RequestDetails; 